import type { ReactNode } from "react";

import type { RebateTier } from "../types";
import type { RebateTierTranslatedContent } from "../useRebateTierTranslatedContent";

import { Button } from "../../Button";
import { ChevronRightIcon } from "../../Icons";
import { Typo } from "../../Typo";
import { formatRebateRate } from "../formatVolume";
import { TierBadge } from "./TierBadge";

type TierHeaderProps = {
  tier: RebateTier;
  content: RebateTierTranslatedContent;
  tierAccessory?: ReactNode;
  onLearnMore: () => void;
  onViewDetails: () => void;
};

type TierHeaderRowProps = {
  label: string;
  children: ReactNode;
  actionLabel: string;
  actionTestId: string;
  onAction: () => void;
};

const TierHeaderRow = ({
  label,
  children,
  actionLabel,
  actionTestId,
  onAction,
}: TierHeaderRowProps) => (
  <div className="flex items-center gap-1 lg:min-w-0 lg:flex-1 lg:flex-col lg:items-stretch">
    <div className="flex min-w-0 flex-1 flex-col gap-2 lg:flex-none">
      <Typo
        element="p"
        smType="labelSM"
        lgType="labelMD"
        textColor="text-text-primary"
      >
        {label}
      </Typo>
      <div className="lg:flex lg:h-12 lg:flex-col lg:justify-center">
        {children}
      </div>
    </div>
    <button
      type="button"
      data-testid={actionTestId}
      onClick={onAction}
      className="flex shrink-0 items-center gap-1 rounded text-text-secondary hover:text-text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-stroke-focused lg:gap-2 lg:self-start"
    >
      <Typo
        element="span"
        smType="bodyXS"
        lgType="bodySM"
        textColor="text-text-secondary"
      >
        {actionLabel}
      </Typo>
      <ChevronRightIcon
        aria-hidden="true"
        className="h-3 w-3 shrink-0 lg:h-4 lg:w-4 [&_path]:stroke-icon-tertiary"
      />
    </button>
  </div>
);

const RebateValue = ({
  tier,
  content,
}: Pick<TierHeaderProps, "tier" | "content">) => {
  const maxRebate =
    tier.maxRebatePerLot === null
      ? "-"
      : formatRebateRate(tier.maxRebatePerLot);

  return (
    <div className="flex items-baseline gap-1">
      <Typo
        element="span"
        smType="labelMD"
        lgType="labelXL"
        textColor="text-text-primary"
      >
        {`${content.upTo} ${maxRebate}`}
      </Typo>
      <Typo
        element="span"
        smType="label2XS"
        lgType="labelSM"
        textColor="text-text-primary"
      >
        {tier.maxRebateCurrency &&
          content.currencyPerLot(tier.maxRebateCurrency)}
      </Typo>
    </div>
  );
};

export const TierHeader = ({
  tier,
  content,
  tierAccessory,
  onLearnMore,
  onViewDetails,
}: TierHeaderProps) => {
  if (!tier.tierVisible) {
    return (
      <div className="flex items-center justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-2">
          <Typo
            element="p"
            smType="labelSM"
            lgType="labelMD"
            textColor="text-text-primary"
          >
            {content.rebate}
          </Typo>
          <RebateValue tier={tier} content={content} />
        </div>
        <Button
          variant="outline"
          size="md"
          internalClassName="typo-body-xs lg:typo-label-sm"
          data-testid="rebate_tier_header_view_details_button"
          onClick={onViewDetails}
        >
          {content.viewDetails}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
      <TierHeaderRow
        label={content.currentTier}
        actionLabel={content.learnMore}
        actionTestId="rebate_tier_header_learn_more_button"
        onAction={onLearnMore}
      >
        <div className="flex items-center gap-2">
          <TierBadge tierKey={tier.tierKey} tierName={tier.tierName} />
          {tierAccessory}
        </div>
      </TierHeaderRow>

      <div aria-hidden className="h-px w-full bg-stroke-primary lg:hidden" />

      <TierHeaderRow
        label={content.rebate}
        actionLabel={content.viewDetails}
        actionTestId="rebate_tier_header_view_details_button"
        onAction={onViewDetails}
      >
        <RebateValue tier={tier} content={content} />
      </TierHeaderRow>
    </div>
  );
};
